import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MediaQuery from 'react-responsive';
import Slider from 'react-slick';
import { v4 as uuid } from 'uuid';
import 'slick-carousel/slick/slick.css';
import withWeatherData from '../withWeatherData';
import HourlyForecastHour from './hourlyForecastHour';
import './hourlyForecast.scss';
import '../carousel.scss';

const HourlyForecast = (props) => {
  const { data } = props;
  const [mode, updateMode] = useState('temp');

  const setTempMode = () => {
    updateMode('temp');
  };

  const setPrecipMode = () => {
    updateMode('precip');
  };

  const temps = data.map((hour) => hour.temp);
  const precips = data.map((hour) => hour.precip);

  /**
   * Scales values for height on temp bars based on temps in data
   * @param {array} values an array of numbers
   * @param {number} scaledMin starting number of scale
   * @param {number} scaledMax ending number of scale
   */

  function scaleBetween(values, scaledMin, scaledMax) {
    const max = Math.max(...values);
    const min = Math.min(...values);

    // eslint-disable-next-line max-len, no-mixed-operators
    return values.map((num) => (scaledMax - scaledMin) * (num - min) / (max - min) + scaledMin);
  }

  const scale = scaleBetween(temps, 5, 80);

  /**
   * Returns a number that is a percentage of the range between minimum and maximum values,
   * used to set the bar height in the hourly forecast chart.
   * @param {number|string} value the value to compute the percentage of min/max
   * @param {array} values the array of values to use
   */
  const getBarHeight = (value, values, type, tempScale) => {
    const valueInt = parseInt(value, 10);
    if ('temp' === type) {
      return Math.floor(tempScale);
    }
    return valueInt;
  };

  const settings = {
    dots: false,
    arrows: true,
    infinite: false,
    draggable: true,
    swipeToSlide: true,
    slidesToShow: 11,
    slidesToScroll: 11,
  };

  const hours = data.map((hour, index) => (
    <HourlyForecastHour
      dayOfWeek={hour.dayOfWeek}
      hourNum={hour.hourNum}
      iconCode={hour.iconCode}
      mode={mode}
      precip={hour.precip}
      // eslint-disable-next-line max-len
      precipBarHeight={5 < getBarHeight(hour.precip, precips, 'precip') * 0.8 ? getBarHeight(hour.precip, precips, 'precip') * 0.8 : 5}
      temp={hour.temp}
      tempBarHeight={getBarHeight(hour.temp, temps, 'temp', scale[index])}
      time={hour.time}
      key={uuid()}
    />
  ));

  const {
    i18n: {
      __,
    },
  } = wp;

  return (
    <Fragment key="hourlyForecast__wrapper">
      {!! data.length && (
        <div className="hourlyForecast__wrapper">
          <div className="hourlyForecast__selector">
            <button
              className={classNames(
                'hourlyForecast__selector-button',
                { current: 'temp' === mode },
              )}
              onClick={setTempMode}
              type="button"
            >
              {__('Temperature', 'nbc')}
            </button>
            <button
              className={classNames(
                'hourlyForecast__selector-button',
                { current: 'precip' === mode },
              )}
              onClick={setPrecipMode}
              type="button"
            >
              {__('Precipitation', 'nbc')}
            </button>
          </div>
          <div className="hourlyForecast__hours">
            <div className="hourlyForecast__hours-inner radar-open">
              {hours}
            </div>
            <MediaQuery maxWidth={1023}>
              <div className="hourlyForecast__hours-inner">
                {hours}
              </div>
            </MediaQuery>
            <MediaQuery minWidth={1024}>
              <Slider {...settings}>
                {hours}
              </Slider>
            </MediaQuery>
          </div>
        </div>
      )}
    </Fragment>
  );
};

HourlyForecast.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    hourNum: PropTypes.string,
    dayOfWeek: PropTypes.string,
    time: PropTypes.string,
    temp: PropTypes.string,
    iconCode: PropTypes.string,
    precip: PropTypes.string,
  })).isRequired,
};

const wrapWithWeatherData = withWeatherData('hourly');
export default wrapWithWeatherData(HourlyForecast);
